import { Card, CardContent, Box, Typography, experimentalStyled, Select, MenuItem } from "@material-ui/core";
import { Download, Info, Notifications, People, Star } from "@material-ui/icons";
import Review from '@material-ui/icons/RateReview';
import { FC, useEffect, useState } from "react";
import { format } from 'date-fns';

const iconMap: Record<string, React.ElementType> = {
  People: People,
  Download: Download,
  Notifications: Notifications,
  Review: Review,
  Info: Info
};

type ReviewType = {
  authorName: string;
  starRating: string | number;
  detailedReview: string;
  datePosted: any;
};

type StatCardProps = {
  cardTitle: string;
  icon: string;
  statCount: number |  string;
  backgroundColor: string | null;
  cardBorderColor?: string | null;
};

type ReviewsCardProps = {
  title: string;
  reviews: ReviewType[];
};

interface LabelRootProps {
  styleProps: {
    color: string;
  };
};

const RatingLabelRoot = experimentalStyled('span')<LabelRootProps>(
  (
    ({ theme, styleProps }) => {

      return {
        alignItems: 'center',
        backgroundColor: styleProps.color,
        borderRadius: theme.shape.borderRadius,
        color: '#FFF',
        cursor: 'default',
        display: 'inline-flex',
        flexGrow: 0,
        flexShrink: 0,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.pxToRem(11),
        fontWeight: theme.typography.fontWeightMedium,
        justifyContent: 'center',
        letterSpacing: 0.5,
        minWidth: 20,
        paddingBottom: theme.spacing(0.5),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        paddingTop: theme.spacing(0.5),
        textTransform: 'uppercase',
        whiteSpace: 'nowrap'
      };
    }
  )
);

const getRatingColor = (givenRating) => {
  switch (givenRating) {
    case 5:
      return '#006400'; // Dark green
    case 4:
      return '#228B22'; // Medium green
    case 3:
      return '#DAA520'; // Goldenrod (dark yellow)
    case 2:
      return '#FF8C00'; // Dark orange
    case 1:
      return '#8B0000'; // Dark red
    default:
      return 'grey';    // Default grey
  }
}

const getRatingLabel = (rating) => {
  const ratingColor = getRatingColor(rating);
  return (
    <RatingLabelRoot
      styleProps={{ color: ratingColor}}
    >
      <>
        {rating} <Star sx={{ fontSize: '11px'}} />
      </>
    </RatingLabelRoot>
  );
};

export const StatCard: FC<StatCardProps> = ({ cardTitle, icon, statCount, backgroundColor='#FFFFFF', cardBorderColor='#000' }) => {
  const IconComponent = iconMap[icon] || Info;
  return (
    <Card sx={{ borderRadius: '16px', border: `1px solid ${cardBorderColor}` }}>
      <CardContent sx={{ backgroundColor: backgroundColor }}>
        <Box display='flex' justifyContent='center' alignItems='center'>
          <Box display='flex' flexDirection='column' alignItems='center' marginRight='10%'>
            <Typography variant='h5'>{cardTitle}</Typography>
            <IconComponent />
          </Box>
          <Box display='flex' alignItems='center'>
            <Typography variant='h4'>{statCount}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const ReviewsCard: React.FC<ReviewsCardProps> = ({ title, reviews = [] }) => {
  const scrollableBoxHeight = '240px';
  const [selectedSorting, setSelectedSorting] = useState('latest');
  const [showMore, setShowMore] = useState(false);
  const [sortedReviews, setSortedReviews] = useState<ReviewType[]>(reviews);

  useEffect(() => {
    let sorted: ReviewType[] = [];
  
    switch (selectedSorting) {
      case 'latest':
        sorted = [...reviews].sort((a, b) => {
          return new Date(b.datePosted).getTime() - new Date(a.datePosted).getTime();
        });
        break;
      case 'oldest':
        sorted = [...reviews].sort((a, b) => {
          return new Date(a.datePosted).getTime() - new Date(b.datePosted).getTime();
        });
        break;
      case 'highestRating':
        sorted = [...reviews].sort((a, b) => Number(b.starRating) - Number(a.starRating));
        break;
      case 'lowestRating':
        sorted = [...reviews].sort((a, b) => Number(a.starRating) - Number(b.starRating));
        break;
      default:
        sorted = [...reviews];
    }
  
    setSortedReviews(sorted);
  }, [selectedSorting, reviews]);

  const toggleShowMore = () => {
    setShowMore(!showMore);
  };

  return (
    <Card sx={{ borderRadius: '16px', width: '100%' }}>
      <CardContent sx={{ height: '100%' }}>
        <Box display='flex' flexDirection='column' height='100%'>
          <Box mb={2} sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">{title}</Typography>
            <Box sx={{ display: 'flex' }}>
            <Select
              value={selectedSorting}
              onChange={(e) => setSelectedSorting(e.target.value as string)}
              sx={{ minWidth: '150px' }}
            >
              <MenuItem value="latest">Latest</MenuItem>
              <MenuItem value="oldest">Oldest</MenuItem>
              <MenuItem value="highestRating">Rating: Highest to Lowest</MenuItem>
              <MenuItem value="lowestRating">Rating: Lowest to Highest</MenuItem>
            </Select>
            </Box>
          </Box>
          <Box sx={{ maxHeight: scrollableBoxHeight, overflowY: 'auto' }}>
            {sortedReviews.slice(0, showMore ? sortedReviews.length : 5).map((review, index) => (
              <Box key={index} mb={1} sx={{
                border: '1px solid grey',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                borderRadius: '4px',
                padding: '8px',
                backgroundColor: 'white',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                }
              }}>
                <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: '1%', paddingRight: '1%' }}>
                  <Typography variant='body1' fontWeight='600' sx={{ color: '#333' }}>
                    {review.authorName}
                  </Typography>
                  <Typography variant='caption'>
                    {format(review.datePosted, 'MMMM dd, yyyy')}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>{getRatingLabel(review.starRating)}</Typography>
                </span>
                <div style={{
                  marginTop: '4px',
                  paddingLeft: '1%',
                  paddingRight: '1%',
                  position: 'relative',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  lineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  <Typography variant='body2' color='text.secondary'>
                    {review.detailedReview}
                  </Typography>
                </div>
              </Box>                
            ))}
            {sortedReviews.length > 5 && !showMore && (
              <Box key={`${title}-read-more`} mb={1} sx={{
                border: '1px solid grey',
                borderRadius: '4px',
                padding: '8px',
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: 'white',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                }
              }}>
                <Typography variant='button' sx={{ cursor: 'pointer', fontSize: '0.7em' }} onClick={toggleShowMore}>
                  Read more...
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const AboutCard: FC = () => {
  return (
    <Card style={{ height: '100%', borderRadius: '16px', border: '1px solid black' }}>
      <CardContent>
        <Box display='flex' flexDirection='column' height='100%'>
          <Box mb={2}>
            <Typography variant="h6">About</Typography>
          </Box>
          <Box padding='2%' paddingTop='0'>
            <Typography variant="body1" paragraph>
              Welcome to InvestApp!
            </Typography>
            <Typography variant="body1" paragraph>
              Our dashboard serves as a powerful tool for both investors and advisors, offering an intuitive and secure environment to monitor and manage financial portfolios, access important documents, and stay updated with real-time financial insights.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Key Features:</strong>
            </Typography>
            <ul>
              <li>Portfolio Tracking: Stay on top of your investments with updated data and analytics, giving you a clear view of your and your clients' financial health and performance.</li>
              <li>Alerts and Notifications: Send timely alerts and notifications tailored to your preferences, keeping your clients informed about significant portfolio changes and market movements.</li>
              <li>User-Friendly Interface: Navigate through your financial data with ease using our intuitive interface, designed to provide a seamless user experience on mobile devices.</li>
            </ul>
            <Typography variant="body1" paragraph>
              Our mission is to empower you with the tools and information you need to make informed financial decisions, all from the convenience of your mobile device. Our dashboard is designed to support your financial journey with efficiency and ease.
            </Typography>
            <Typography variant="body1">
              Welcome to the future of fintech portfolio management.
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
